import type { ComponentPropsWithRef, ReactNode } from 'react';
import { useTranslation } from '../../../i18n/LocaleProvider';

import { cn } from '../../../lib/cn';
import { Spinner } from '../../core/Spinner';
import { Field, fieldDescribedBy } from './Field';
import { controlBoxClass } from './controlStyles';
import type { ControlSize } from './controlStyles';
import { useField } from './useField';

/* =============================================================================
   InputEmail — text constrained to an email shape.
   -----------------------------------------------------------------------------
   Same box and the same state ladder as `Input`; the difference is the validator
   it hands `useField`. The rule is a shape check, not a deliverability check:

     - local part — one or more characters that are neither whitespace nor `@`,
                    so a space and a second `@` are both rejected outright.
     - domain     — one or more `[A-Za-z0-9-]` labels, each closed by a dot, and
                    a final label of at least two letters.

   That rejects `a@b`, `a@.com`, `a@b.`, `a@@b.com`, `a@b-.com` and
   `mailto:a@b.com` (the local part excludes `:`), and accepts `a@b.co` and
   `first.last+tag@sub.domain.com`.
   ============================================================================= */

const EMAIL_PATTERN = /^[^\s@:]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

/**
 * Whether a value is an email address, by the one rule this module defines.
 *
 * Exported because a caller that validates the same field at submit time — to
 * block a form before anything runs — would otherwise keep a second copy of the
 * pattern, and two copies are two verdicts: a field that renders an error while
 * submission proceeds, or a submission refused with no error to explain why.
 */
export function isEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value);
}

/**
 * The message shown when invalid, or `null` when valid. An empty value never
 * reaches here. Surrounding whitespace is trimmed by the blur handler, so this
 * tests the value it is actually given rather than a laundered copy of it.
 *
 * The wording arrives as an argument: this is a module-level function with no
 * translator, and a message frozen here would keep the language it was written
 * in on a page the reader has since switched.
 */
function validateEmail(value: string, message: string): string | null {
  return isEmail(value) ? null : message;
}

export interface InputEmailProps
  extends Omit<ComponentPropsWithRef<'input'>, 'value' | 'onChange' | 'id' | 'size' | 'required' | 'children'> {
  id: string;
  label?: ReactNode;
  description?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  /** Extra type validation, on top of the component's own. */
  validate?: (value: string) => string | null;
  /** A submit-level or server error. Wins over the component's own validation. */
  error?: string | null;
  /** Raises the inline-save "Saved!" state. */
  success?: boolean;
  successMessage?: ReactNode;
  size?: ControlSize;
  loading?: boolean;
  /** Bumped by the form on a submit attempt, to reveal errors. */
  submitCount?: number;
}

/** Room for the absolutely positioned spinner, so the text never runs under it. */
const LOADING_PAD: Record<ControlSize, string> = {
  sm: 'pr-8',
  md: 'pr-9',
  lg: 'pr-10',
};

export function InputEmail({
  id,
  label,
  description,
  value,
  onChange,
  required = false,
  validate,
  error,
  success = false,
  successMessage,
  size = 'md',
  loading = false,
  submitCount,
  disabled,
  readOnly,
  onBlur,
  type = 'email',
  inputMode = 'email',
  autoComplete = 'email',
  className,
  ref,
  ...rest
}: InputEmailProps) {
  const { t } = useTranslation();
  // The component's own rule runs first; the consumer's `validate` is extra —
  // which means this rule is the one that renders, so its message is the one
  // that has to follow the interface language.
  const field = useField({
    value,
    onChange,
    validate: (next) => validateEmail(next, t('ds.form.invalidEmail')) ?? validate?.(next) ?? null,
    required,
    submitCount,
    success,
  });
  const message = error ?? field.error;
  const invalid = Boolean(message);
  const savedMessage = field.success ? (successMessage ?? 'Saved!') : undefined;
  const describedBy = fieldDescribedBy(id, {
    description: Boolean(description),
    message: Boolean(message ?? savedMessage),
  });

  const control = (
    <input
      {...rest}
      ref={ref}
      type={type}
      inputMode={inputMode}
      autoComplete={autoComplete}
      id={id}
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
      onBlur={(event) => {
        // An address pasted from a mail client arrives with whitespace round it,
        // and the field would otherwise validate one string while emitting
        // another. Trimming here is what keeps the two the same.
        if (value !== value.trim()) onChange(value.trim());
        // `...rest` is spread first, so the consumer's own `onBlur` would
        // otherwise be silently replaced by this one.
        onBlur?.(event);
        field.onBlur();
      }}
      disabled={disabled}
      readOnly={readOnly}
      // Deliberate: the native `required` attribute is NOT set — the form is
      // `noValidate` and this library's own error UI owns validation; `aria-required` carries the semantics.
      aria-required={required || undefined}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      aria-busy={loading || undefined}
      className={cn(
        controlBoxClass({ size, invalid, success: Boolean(savedMessage), disabled, readOnly }),
        loading && LOADING_PAD[size],
        className,
      )}
    />
  );

  return (
    <Field
      id={id}
      label={label}
      description={description}
      required={required}
      disabled={disabled}
      invalid={invalid}
      error={message}
      successMessage={savedMessage}
      size={size}
    >
      {loading ? (
        <div className="relative w-full min-w-0">
          {control}
          <Spinner
            size="sm"
            tone="muted"
            aria-hidden="true"
            className="absolute right-2.5 top-1/2 z-20 -translate-y-1/2"
          />
        </div>
      ) : (
        control
      )}
    </Field>
  );
}
